import React from "react";
import classnames from "classnames";

import { CATEGORY_NAMES } from "@/constants/dispensary";
import { Action, Category } from "@/constants/events";
import { MappedFilterOption } from "@/custom-types/Shop";
import { useEventTracker } from "@/hooks/useEventTracker";
import useFilters from "@/hooks/useFilters";
import { isCategoryHub } from "@/hooks/useShop";
import useShopSearchParams from "@/hooks/useShopSearchParams";

import SearchFiltersPlaceholder from "@/components/SearchFilter/SearchFiltersPlaceholder";

import FilterPill from "../FilterPill";

type Props = {
  className?: string;
  filters: MappedFilterOption[];
  selectionId?: number;
  loading?: boolean;
  showEmptyState?: boolean;
};

const FilterBar: React.FC<Props> = ({
  className,
  filters,
  selectionId,
  loading,
  showEmptyState,
}) => {
  const { publishEvent } = useEventTracker();
  const query = useShopSearchParams();
  const { selectFilterValue, addFilter } = useFilters();

  const { shopCategory } = query;

  if (isCategoryHub(query) || showEmptyState) {
    return null;
  }

  const categoryOptions =
    filters.find((filter) => filter.name === "product_category")?.values ?? [];
  const sortedCategories = categoryOptions
    .filter((category) => category in CATEGORY_NAMES)
    .sort(
      (a, b) =>
        Object.keys(CATEGORY_NAMES).indexOf(a) -
        Object.keys(CATEGORY_NAMES).indexOf(b),
    );

  const searchCategory = selectFilterValue("product_category");

  const handleClick = (e: React.MouseEvent<HTMLElement>) => {
    const value = e.currentTarget?.dataset?.value;
    addFilter({
      product_category: value,
    });

    publishEvent({
      action: Action.click,
      brandId: shopCategory?.includes("brand") ? selectionId : undefined,
      category: Category.productFilterBar,
      label: `add/remove ${value?.toLowerCase() || "all products"}`,
      strainId: shopCategory?.includes("strain") ? selectionId : undefined,
    });
  };

  return loading ? (
    <SearchFiltersPlaceholder />
  ) : sortedCategories?.length ? (
    <div className={classnames(className)}>
      <div className="font-bold text-xs mb-xs">Show me</div>
      <div
        id="shop-filter-bar"
        className="flex lg:flex-wrap overflow-x-scroll lg:overflow-x-auto"
      >
        <FilterPill
          isAllResults={true}
          onClick={handleClick}
          activeCategory={searchCategory}
        >
          All results
        </FilterPill>
        {sortedCategories.map((category) => (
          <FilterPill
            key={category}
            onClick={handleClick}
            activeCategory={searchCategory}
            category={category}
          >
            {CATEGORY_NAMES[category] || category}
          </FilterPill>
        ))}
      </div>
    </div>
  ) : (
    <></>
  );
};

export default FilterBar;
